import { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Grid, 
  Autocomplete,
  MenuItem,
  Select,
  FormControl,
  Checkbox,
  Chip
} from '@mui/material';
import { CheckBox as CheckBoxIcon, CheckBoxOutlineBlank } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { LoadingButton } from '@mui/lab';
import { CaseStatus } from '@bandido/shared/constants';
import { UserNotificationTrigger, UserNotificationTriggerFormData } from '../../types';
import { useGetAllClients } from '@frontend/features/clients/queries';
import { useGetAllUsers } from '@frontend/features/users/queries';
import { useGetAllWorkflowsQuery, useGetStepsByManyWorkflowIds } from '@frontend/features/workflows/queries';

interface TriggerFormProps {
  trigger?: UserNotificationTrigger;
  onSubmit: (data: UserNotificationTriggerFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export const TriggerForm = ({ trigger, onSubmit, onCancel, isLoading = false }: TriggerFormProps) => {
  const { t } = useTranslation();

  // Estados para búsqueda en autocompletes
  const [clientSearch, setClientSearch] = useState('');
  const [agentSearch, setAgentSearch] = useState('');
  const [workflowSearch, setWorkflowSearch] = useState('');
  const [stepSearch, setStepSearch] = useState('');

  // Estados para formulario y selecciones
  const [formData, setFormData] = useState<UserNotificationTriggerFormData>({
    clientId: trigger?.clientId || undefined,
    agentId: trigger?.agentId || undefined,
    workflowId: trigger?.workflowId || undefined,
    stepId: trigger?.stepId || undefined,
    status: trigger?.status || undefined,
    caseId: trigger?.caseId || undefined,
  });

  const [selectedWorkflows, setSelectedWorkflows] = useState<Array<{id: string, name: string}>>([]);
  const [availableSteps, setAvailableSteps] = useState<Array<{id: string, name: string}>>([]);
  const [isInitialized, setIsInitialized] = useState(false);



  // Queries para obtener datos reales
  const { data: clientList } = useGetAllClients({
    pageSize: 50,
    currentPage: 1,
    search: clientSearch || '',
    clientName: '',
    sort: 'firstName',
    order: 'ASC',
  });

  const { data: agentList } = useGetAllUsers({
    pageSize: 50,
    currentPage: 1,
    search: agentSearch || '',
  });



  const { data: workflows } = useGetAllWorkflowsQuery({
    pageSize: 50,
    currentPage: 1,
    search: workflowSearch,
  });

  const { data: steps } = useGetStepsByManyWorkflowIds({
    search: stepSearch,
    workflowIds: selectedWorkflows.map(w => w.id),
  });

  // Reset cuando cambia el trigger
  useEffect(() => {
    setIsInitialized(false);
    setSelectedWorkflows([]);
  }, [trigger?.id]);

  // Inicializar datos cuando se está editando
  useEffect(() => {
    if (trigger && workflows?.items && !isInitialized) {
      // Convertir workflowId string (formato: "id1|id2|id3") a array de workflows
      if (trigger.workflowId) {
        const workflowIds = trigger.workflowId.split('|');
        const triggerWorkflows = workflows.items
          .filter(w => workflowIds.includes(w.id))
          .map(w => ({ id: w.id, name: w.name }));
        setSelectedWorkflows(triggerWorkflows);
      }
      setIsInitialized(true);
    }
  }, [trigger?.id, workflows?.items?.length, isInitialized]);

  // Filtrar steps basado en workflows seleccionados
  useEffect(() => {
    if (steps?.length) {
      setAvailableSteps(steps.map(step => ({ id: step.id, name: step.name })));
    } else {
      setAvailableSteps([]);
      setFormData(prev => ({ ...prev, stepId: undefined }));
    }
  }, [steps]);

  const handleWorkflowChange = (workflows: Array<{id: string, name: string}>) => {
    setSelectedWorkflows(workflows);
    const workflowIds = workflows.map(w => w.id).join('|');
    setFormData(prev => ({ 
      ...prev, 
      workflowId: workflowIds || undefined,
      stepId: undefined 
    }));
  };

  const handleSubmit = () => {
    onSubmit(formData);
  };

  const statusList = [
    { id: CaseStatus.canceled, name: t(`cases.status.${CaseStatus.canceled}`) },
    { id: CaseStatus.done, name: t(`cases.status.${CaseStatus.done}`) },
    { id: CaseStatus.inProgress, name: t(`cases.status.${CaseStatus.inProgress}`) },
    { id: CaseStatus.needsAgentAction, name: t(`cases.status.${CaseStatus.needsAgentAction}`) },
    { id: CaseStatus.needsClientAction, name: t(`cases.status.${CaseStatus.needsClientAction}`) },
    { id: CaseStatus.open, name: t(`cases.status.${CaseStatus.open}`) },
  ];

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={3}>
        {t('userNotificationTriggers.formDescription')}
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" alignSelf="stretch">
            <Typography variant="subtitle2" gutterBottom>
              {t('userNotificationTriggers.client')}
            </Typography>
            {formData.clientId && (
              <Button
                size="small"
                color="error"
                variant="text"
                onClick={() => {
                  setFormData(prev => ({ ...prev, clientId: undefined }));
                  setClientSearch('');
                }}
                sx={{ minWidth: 'auto', p: 0 }}
              >
                {t('filters.clear')}
              </Button>
            )}
          </Box>
          <Autocomplete
            fullWidth
            size="small"
            options={clientList?.items || []}
            getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
            value={
              formData.clientId 
                ? clientList?.items?.find(c => c.id === formData.clientId) || null
                : null
            }
            onChange={(_, value) => {
              setFormData(prev => ({ 
                ...prev, 
                clientId: value?.id 
              }));
              // Limpiar búsqueda cuando se selecciona una opción
              if (value) {
                setClientSearch('');
              }
            }}
            onInputChange={(_, value) => setClientSearch(value)}
            noOptionsText={t('common.noData')}
            renderInput={(params) => (
              <TextField {...params} placeholder={t('userNotificationTriggers.selectClient')} />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" alignSelf="stretch">
            <Typography variant="subtitle2" gutterBottom>
              {t('userNotificationTriggers.agent')}
            </Typography>
            {formData.agentId && (
              <Button
                size="small"
                color="error"
                variant="text"
                onClick={() => {
                  setFormData(prev => ({ ...prev, agentId: undefined }));
                  setAgentSearch('');
                }}
                sx={{ minWidth: 'auto', p: 0 }}
              >
                {t('filters.clear')}
              </Button>
            )}
          </Box>
          <Autocomplete
            fullWidth
            size="small"
            options={agentList?.items || []}
            getOptionLabel={(option) => `${option.firstName} ${option.lastName}`}
            value={
              formData.agentId 
                ? agentList?.items?.find(a => a.id === formData.agentId) || null
                : null
            }
            onChange={(_, value) => {
              setFormData(prev => ({ 
                ...prev, 
                agentId: value?.id 
              }));
              // Limpiar búsqueda cuando se selecciona una opción
              if (value) {
                setAgentSearch('');
              }
            }}
            onInputChange={(_, value) => setAgentSearch(value)}
            noOptionsText={t('common.noData')}
            renderInput={(params) => (
              <TextField {...params} placeholder={t('userNotificationTriggers.selectAgent')} />
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" alignSelf="stretch">
            <Typography variant="subtitle2" gutterBottom>
              {t('userNotificationTriggers.workflow')}
            </Typography>
            {selectedWorkflows.length > 0 && (
              <Button
                size="small"
                color="error"
                variant="text"
                onClick={() => {
                  setSelectedWorkflows([]);
                  setFormData(prev => ({ ...prev, workflowId: undefined, stepId: undefined }));
                  setWorkflowSearch('');
                }}
                sx={{ minWidth: 'auto', p: 0 }}
              >
                {t('filters.clear')}
              </Button>
            )}
          </Box>
          <Autocomplete
            fullWidth
            size="small"
            multiple
            options={workflows?.items || []}
            getOptionLabel={(option) => option.name}
            value={selectedWorkflows}
            onChange={(_, value) => handleWorkflowChange(value.map(w => ({ id: w.id, name: w.name })))}
            onInputChange={(_, value) => setWorkflowSearch(value)}
            clearText={t('common.clear')}
            noOptionsText={t('common.noData')}
            renderInput={(params) => (
              <TextField {...params} placeholder={t('userNotificationTriggers.selectWorkflow')} />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.id}
                  label={option.name}
                  size="small"
                />
              ))
            }
            renderOption={(props, option, { selected }) => (
              <li {...props} key={option.id}>
                <Checkbox
                  icon={<CheckBoxOutlineBlank fontSize="small" />}
                  checkedIcon={<CheckBoxIcon fontSize="small" />}
                  sx={{ mr: 1 }}
                  checked={selected}
                />
                {option.name}
              </li>
            )}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" alignSelf="stretch">
            <Typography variant="subtitle2" gutterBottom>
              {t('userNotificationTriggers.step')}
            </Typography>
            {formData.stepId && (
              <Button
                size="small"
                color="error"
                variant="text"
                onClick={() => {
                  setFormData(prev => ({ ...prev, stepId: undefined }));
                  setStepSearch('');
                }}
                sx={{ minWidth: 'auto', p: 0 }}
              >
                {t('filters.clear')}
              </Button>
            )}
          </Box>
          <Box sx={{ opacity: availableSteps.length === 0 ? 0.5 : 1 }}>
            <Autocomplete
              fullWidth
              size="small"
              disabled={availableSteps.length === 0}
              options={availableSteps}
              getOptionLabel={(option) => option.name}
              value={
                formData.stepId 
                  ? availableSteps.find(s => s.id === formData.stepId) || null
                  : null
              }
              onChange={(_, value) => setFormData(prev => ({ 
                ...prev, 
                stepId: value?.id 
              }))}
              onInputChange={(_, value) => setStepSearch(value)}
              noOptionsText={t('common.noData')}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  placeholder={
                    availableSteps.length === 0 
                      ? t('userNotificationTriggers.selectWorkflowFirst')
                      : t('userNotificationTriggers.selectStep')
                  } 
                />
              )}
            />
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" alignSelf="stretch">
            <Typography variant="subtitle2" gutterBottom>
              {t('userNotificationTriggers.status')}
            </Typography>
            {formData.status && (
              <Button
                size="small"
                color="error"
                variant="text"
                onClick={() => {
                  setFormData(prev => ({ ...prev, status: undefined }));
                }}
                sx={{ minWidth: 'auto', p: 0 }}
              >
                {t('filters.clear')}
              </Button>
            )}
          </Box>
          <FormControl fullWidth size="small">
            <Select
              value={formData.status || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value || undefined }))}
              displayEmpty
            >
              <MenuItem value="">
                <em>{t('userNotificationTriggers.selectStatus')}</em>
              </MenuItem>
              {statusList.map((status) => (
                <MenuItem key={status.id} value={status.id}>
                  {status.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" alignSelf="stretch">
            <Typography variant="subtitle2" gutterBottom>
              {t('userNotificationTriggers.specificCase')}
            </Typography>
            {formData.caseId && (
              <Button
                size="small"
                color="error"
                variant="text"
                onClick={() => {
                  setFormData(prev => ({ ...prev, caseId: undefined }));
                }}
                sx={{ minWidth: 'auto', p: 0 }}
              >
                {t('filters.clear')}
              </Button>
            )}
          </Box>
          <TextField
            fullWidth
            size="small"
            type="number"
            placeholder={t('userNotificationTriggers.caseIdPlaceholder')}
            value={formData.caseId || ''}
            onChange={(e) => setFormData(prev => ({ 
              ...prev, 
              caseId: e.target.value ? Number(e.target.value) : undefined 
            }))}
          />
        </Grid>
      </Grid>

      <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
        <Button variant="outlined" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <LoadingButton
          variant="contained"
          loading={isLoading}
          onClick={handleSubmit}
        >
          {trigger ? t('common.update') : t('common.actions.add')}
        </LoadingButton>
      </Box>
    </Box>
  );
};
